import * as React from 'react';

import user from 'js/lib/user';

import { getS12nPaymentsDisplayName } from 'bundles/s12n-common/lib/s12nProductVariantUtils';
import { CourseraMetatags } from 'bundles/seo';
import S12nXDPSchemaMarkup from 'bundles/seo/components/S12nXDPSchemaMarkup';
import {
  PROF_CERTS_USING_SKILL_CERT_LABEL,
  XDP_META_DESC_LENGTH_LIMIT,
  getXdpMetaDescriptionSuffix,
} from 'bundles/unified-description-page-common/constants';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import type { DomainFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import type { PartnerFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import getSEODescription from 'bundles/unified-description-page-common/utils/getSEODescription';

import _t from 'i18n!nls/s12n';

function getSeoInformation({
  title,
  headline,
  subheader = '',
  partners,
  coBrandingEnabled,
  slug,
}: {
  title: string;
  headline?: string | null;
  subheader?: string | null;
  partners?: Array<PartnerFragment> | null;
  coBrandingEnabled?: boolean | null;
  slug?: string;
}) {
  let metaDescription = '';

  if (headline) {
    metaDescription += headline;
  }

  if (subheader) {
    metaDescription = metaDescription.trim().length > 0 ? metaDescription + '. ' + subheader : subheader;
  }

  const applySkillCertificatePCDPLabel = slug && PROF_CERTS_USING_SKILL_CERT_LABEL.includes(slug);

  return {
    title: applySkillCertificatePCDPLabel
      ? title.replace(_t('Professional Certificate'), _t('Skill Certificate'))
      : title,
    description: getSEODescription(metaDescription, partners, !!coBrandingEnabled),
  };
}

type Props = {
  pageData: ConsumerSpecializationPageFieldsFragment | EnterpriseSpecializationPageFieldsFragment;
  disableCrawlerIndexing?: boolean;
  enableCrawlerIndexing?: boolean;
  paginationProperties?: {
    canonicalLinkHrefOverride?: string;
  };
};

const SpecializationAndProfessionalCertificatePageSEO = ({
  disableCrawlerIndexing,
  pageData,
  paginationProperties,
  enableCrawlerIndexing,
}: Props) => {
  const {
    slug,
    name,
    headline,
    subheader,
    primaryLanguages,
    partners,
    domains,
    metaImgSrc,
    productVariant,
    coBrandingEnabled,
    courses,
    skills,
    totalLearningMonths,
    ratings,
    difficultyLevel,
  } = pageData;

  const seoData = getSeoInformation({
    title: getS12nPaymentsDisplayName(name, productVariant),
    headline,
    subheader,
    partners,
    coBrandingEnabled,
    slug,
  });

  const domain: DomainFragment | undefined = domains?.[0];
  const isConsumerSpecializationPage = pageData.__typename === 'DescriptionPage_SpecializationPage';

  return (
    <>
      {!user.isAuthenticatedUser() && (
        <S12nXDPSchemaMarkup
          domain={domain}
          slug={slug}
          name={name}
          skills={skills}
          description={seoData.description}
          partners={partners}
          // @ts-expect-error URL: string
          logo={
            isConsumerSpecializationPage
              ? pageData.cardImageUrl ?? partners?.[0].rectangularLogo
              : partners?.[0].rectangularLogo
          }
          primaryLanguage={primaryLanguages[0]}
          timeRequiredInMonths={totalLearningMonths}
          courses={courses}
          enableCoBranding={!!coBrandingEnabled}
          productVariant={productVariant}
          difficultyLevel={difficultyLevel}
          ratings={ratings}
        />
      )}
      <CourseraMetatags
        metaNameAndProperties={{
          title: seoData.title,
          description: seoData.description,
          ogAndTwitterTitle: name,
          absoluteImageHref: metaImgSrc ?? undefined,
          twitterCardWithLargeImage: true,
          disableCourseraDescriptionSuffix: true,
          descriptionLengthLimit: XDP_META_DESC_LENGTH_LIMIT - getXdpMetaDescriptionSuffix().length,
          descriptionSuffix: getXdpMetaDescriptionSuffix(),
          disableCrawlerIndexing,
          enableCrawlerIndexing,
        }}
        {...(disableCrawlerIndexing && { hrefLangOverrides: () => [] })}
        paginationProperties={paginationProperties}
      />
    </>
  );
};

export default SpecializationAndProfessionalCertificatePageSEO;
